import { Button, Typography } from '@mui/material'
import { DataGrid, type GridColDef, GridOverlay } from '@mui/x-data-grid'
import React, { useMemo } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'

import { TruncatedTextWithTooltip } from 'components/common/TruncatedTextWithTooltip'
import Icon from 'components/Icon'
import { type TestSuiteFormValues } from 'components/test-suites/forms/test-suite-form-utils'
import { useTranslation } from 'hooks/use-translation'
import { type Team } from 'types/api/teams'
import { naturalSortBy } from 'utils/list'
// import { formatDate, formatTime } from 'utils/formatDate'

const TestSuiteTeamTableEmptyState: React.FC = () => {
  const { trans } = useTranslation()

  return (
    <GridOverlay>
      <Typography>{trans('test_suites.team_selection.table.empty')}</Typography>
    </GridOverlay>
  )
}

export const TestSuiteTeamTable: React.FC<{
  canEdit: boolean
  isDefaultTestSuite: boolean
  teams: Team[]
}> = ({ canEdit, isDefaultTestSuite, teams }) => {
  const { trans } = useTranslation()
  const { formState, setValue, getValues } =
    useFormContext<TestSuiteFormValues>()
  const { defaultValues } = formState
  const selectedTeamIds = useWatch({ name: 'targetTeamIds' })

  const selectedTeams = useMemo(() => {
    // pin the newly selected teams to top of list
    const newlySelectedTeams: Team[] = []
    const defaultTeamsSelected = (teams ?? []).filter((team) => {
      const isIncluded = selectedTeamIds?.includes(team.identifier)
      const isPartOfDefaultSelection =
        isIncluded && defaultValues.targetTeamIds?.includes(team.identifier)

      if (isIncluded && !isPartOfDefaultSelection) {
        newlySelectedTeams.push(team)
      }

      return isPartOfDefaultSelection
    })

    return newlySelectedTeams.concat(
      naturalSortBy(defaultTeamsSelected, (o) => o.name)
    )
  }, [defaultValues, selectedTeamIds, teams])

  const handleRemoveTeam = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    team: Team
  ) => {
    e.preventDefault()
    const existingTargetTeamIds = getValues('targetTeamIds')

    const newTargetTeamIds = existingTargetTeamIds.filter(
      (id) => id !== team.identifier
    )

    setValue('targetTeamIds', newTargetTeamIds, { shouldDirty: true })
  }

  const tableColumns: GridColDef<Team>[] = [
    {
      field: 'name',
      headerName: trans('test_suites.team_selection.table.header.name'),
      flex: 5,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      disableReorder: true,
      renderCell: (params) => {
        const name = params.row.name
        return (
          <TruncatedTextWithTooltip
            data-testid={`test-suite-team-table-cell-name-${params.row.identifier}`}
            tooltipProps={{
              title: name
            }}
          >
            {name}
          </TruncatedTextWithTooltip>
        )
      }
    }
    // todo: enable "since" column when that field is available
    // {
    //   field: 'since',
    //   headerName: trans('test_suites.team_selection.table.header.since'),
    //   flex: 1,
    //   sortable: false,
    //   filterable: false,
    //   disableColumnMenu: true,
    //   disableReorder: true,
    //   valueGetter: (params) => params.row.created_at ?? '-',
    //   renderCell: (params) => {
    //     return (
    //       <Typography>
    //         {params.row.created_at
    //           ? `${formatDate(params.row.created_at)} ${formatTime(
    //               params.row.created_at
    //             )}`
    //           : '-'}
    //       </Typography>
    //     )
    //   }
    // }
  ]

  const actionsColumn: GridColDef<Team> = {
    field: '_actions',
    headerName: trans('actions'),
    flex: 1,
    sortable: false,
    filterable: false,
    disableColumnMenu: true,
    disableReorder: true,
    renderCell: (params) => {
      // if isDefaultTestSuite, only allow deletion of teams added while editing
      const isAllowedToDelete =
        !isDefaultTestSuite ||
        !defaultValues.targetTeamIds.includes(params.row.identifier)

      return (
        isAllowedToDelete && (
          <Button
            color="error"
            data-testid={`test-suite-team-table-cell-actions-delete-${params.row.identifier}`}
            size="small"
            startIcon={<Icon icon="xmark" />}
            variant="contained"
            onClick={(e) => handleRemoveTeam(e, params.row)}
          >
            {trans('remove')}
          </Button>
        )
      )
    }
  }

  if (canEdit) {
    tableColumns.push(actionsColumn)
  }

  return (
    <DataGrid<Team>
      autoHeight
      columns={tableColumns}
      components={{
        NoRowsOverlay: TestSuiteTeamTableEmptyState
      }}
      disableSelectionOnClick={true}
      // MUI gives you no direct way to base row styling on the contents of the row
      // but we *can* add a custom class name for it here and then add custom styling for that class below
      // getRowClassName={getRowClassName}
      getRowId={(row) => row.identifier}
      pageSize={10}
      rows={selectedTeams}
      rowsPerPageOptions={[10]}
      sx={{
        '& .MuiDataGrid-cell:focus': {
          outline: 0
        }
      }}
    />
  )
}
